import { Form } from 'antd';
import { get } from 'lodash';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { SplitDescriptions } from '../../../components/SplitDescriptions';
import { TableCellDescriptions } from '../../../components/TableCellDescriptions';
import { UserTimezoneSelect } from '../../../components/UserTimezoneSelect';
import {
  RoleKey_enum,
  UserRoleDetailsFragment,
} from '../../../graphql/hasura/generated';
import { formatTimestamp, USER_TIMEZONE_VALUE } from '../../../utils';
import { getDescriptionsFromColumns } from '../../helpers/getDescriptionsFromColumns';
import * as columns from '../columns';

import { FunnelAppointmentDescriptions } from './FunnelAppointmentDescriptions';
import {
  PropertyPinCodeDescriptions,
  shouldDisplayPropertyPinCode,
} from './PropertyPinCodeDescriptions';
import { ScopeOfAccessDescriptions } from './ScopeOfAccessDescriptions';
import {
  shouldDisplayVendorPasscode,
  VendorPasscodeDescriptions,
} from './VendorPasscodeDescriptions';

interface IUserRoleDetailsBodyProps {
  userRole: UserRoleDetailsFragment;
}

export const UserRoleDetailsBody: React.FC<IUserRoleDetailsBodyProps> = (props) => {
  const { userRole } = props;
  const { role, parentUserRoleId, managedWithinProperty } = userRole;

  const propertyTimezone = managedWithinProperty?.timezone;
  const defaultSelectedTimezone = propertyTimezone || USER_TIMEZONE_VALUE;
  const [selectedTimezone, setSelectedTimezone] = useState(defaultSelectedTimezone);

  return (
    <>
      {propertyTimezone && (
        <Form.Item
          label='Timezone'
          style={{ width: '49%' }}
        >
          <UserTimezoneSelect
            value={selectedTimezone}
            onChange={value => setSelectedTimezone(value)}
            contextualTimezone={{
              value: propertyTimezone,
              formatLabel: (label) => (
                <>{label} — <strong>Property timezone</strong></>
              ),
            }}
          />
        </Form.Item>
      )}
      <SplitDescriptions
        row={userRole}
        leftSideColumns={[
          {
            title: 'Status',
            render: () => (
              <>
                {columns.statusColumn.render(userRole)}
                <div style={{ float: 'right' }}>
                  <TableCellDescriptions
                    descriptions={getDescriptionsFromColumns(userRole, [
                      {
                        title: columns.revokedAtColumn.title,
                        render: ({ revokedAt }) => formatTimestamp(revokedAt, selectedTimezone),
                      },
                      columns.revokedByUserColumn,
                    ])}
                  />
                </div>
              </>
            ),
          },
          columns.createRoleColumn(userRole.role.permissionScope),
          {
            title: 'Scope of Access',
            render: () => <ScopeOfAccessDescriptions userRole={userRole} />,
          },
          columns.assignedToColumn,
          columns.grantedByColumn,
          {
            title: 'Created',
            render: ({ createdAt }) => formatTimestamp(createdAt, selectedTimezone),
          },
        ]}
        rightSideColumns={[
          {
            title: 'Duration of Access',
            render: () => (
              <TableCellDescriptions
                descriptions={getDescriptionsFromColumns(userRole, [
                  {
                    title: columns.activationDateColumn.title,
                    render: ({ activatesAt }) => formatTimestamp(activatesAt, selectedTimezone) || <></>,
                  },
                  {
                    title: columns.expirationDateColumn.title,
                    render: ({ expiresAt }) => formatTimestamp(expiresAt, selectedTimezone) || <></>,
                  },
                ])}
              />
            ),
          },
          userRole.schedule ? columns.scheduleColumn : null,
          {
            title: 'Additional Context',
            render: () => (
              <TableCellDescriptions
                descriptions={getDescriptionsFromColumns(userRole, [
                  columns.sourceIdColumn,
                  columns.grantMethodColumn,
                  columns.reasonColumn,
                  columns.notesColumn,
                ])}
              />
            ),
          },
          shouldDisplayPropertyPinCode(userRole) ? {
            title: 'Property PIN Code',
            render: () => <PropertyPinCodeDescriptions userRole={userRole} />,
          } : null,
          shouldDisplayVendorPasscode(userRole) ? {
            title: 'Vendor Passcode',
            render: () => <VendorPasscodeDescriptions userRole={userRole} tz={selectedTimezone} />,
          } : null,
          get(userRole.sourcePayload, 'funnelAppointmentId') ? {
            title: 'Funnel Appointment',
            render: () => <FunnelAppointmentDescriptions userRole={userRole} tz={selectedTimezone} />
          } : null,
        ]}
      />
      {role.key === RoleKey_enum.SMART_LOCK_UNREGISTERED_USER && parentUserRoleId && (
        <div style={{ paddingTop: 25, clear: 'both' }}>
          <Link to={`/assigned-roles/details/${parentUserRoleId}`}>
            View Property Guest Pass
          </Link>
        </div>
      )}
    </>
  );
};
